import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { BarChart3, BarChart, LineChart, TrendingUp, Users, Activity, Target } from 'lucide-react';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart as RechartsLineChart, Line, Cell, Legend } from 'recharts';

// Hook to detect dark mode
const useIsDarkMode = () => {
  const [isDark, setIsDark] = useState(false);
  
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    
    // Watch for theme changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    return () => observer.disconnect();
  }, []);
  
  return isDark;
};

// Theme-aware colors for charts
const getChartColors = (isDark: boolean) => ({
  text: isDark ? '#E5E7EB' : '#374151',
  textMuted: isDark ? '#9CA3AF' : '#6B7280',
  grid: isDark ? '#374151' : '#E5E7EB',
  tooltipBg: isDark ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.98)',
  tooltipBorder: isDark ? '#4B5563' : '#E5E7EB',
  tooltipText: isDark ? '#F9FAFB' : '#111827',
});

interface ClientBreakdown {
  client: string;
  requirements: number;
  submissions: number;
}

interface FitmentScoreDistribution {
  range: string;
  count: number;
  percentage: number;
}

interface DayWiseSubmissions {
  date: string;
  submissions: number;
}

interface ManagerWiseSubmissions {
  manager: string;
  submissions: number;
}

interface RecruiterPerformance {
  recruiter: string;
  manager: string;
  totalSubmissions: number;
  fitmentScores: {
    '90-100'?: number;
    '80-90'?: number;
    '70-80'?: number;
    '60-70'?: number;
    '50-60'?: number;
    '40-50'?: number;
    '<40'?: number;
  };
}

interface PipelineHealth {
  submitted: number;
  reviewing: number;
  shortlisted: number;
  rejected: number;
  withdrawn: number;
  total: number;
}

interface PeriodComparison {
  current_period: {
    total_submissions: number;
    active_requirements: number;
    avg_fitment_score: number;
    shortlisted: number;
  };
  previous_period: {
    total_submissions: number;
    active_requirements: number;
    avg_fitment_score: number;
    shortlisted: number;
  };
  percentage_change: {
    total_submissions: number;
    active_requirements: number;
    avg_fitment_score: number;
    shortlisted: number;
  };
  trend_direction: {
    total_submissions: 'up' | 'down' | 'stable';
    active_requirements: 'up' | 'down' | 'stable';
    avg_fitment_score: 'up' | 'down' | 'stable';
    shortlisted: 'up' | 'down' | 'stable';
  };
}

interface QualityMetrics {
  avg_fitment_score: number;
  score_trend: number;
  high_quality_rate: number;
  common_skill_gaps: Array<{ skill: string; count: number }>;
  score_vs_outcome_correlation: {
    shortlisted_avg: number;
    rejected_avg: number;
  };
}

interface EnhancedAnalyticsChartsProps {
  clientBreakdown: ClientBreakdown[];
  fitmentScoreDistribution: FitmentScoreDistribution[];
  dayWiseSubmissions: DayWiseSubmissions[];
  managerWiseSubmissions: ManagerWiseSubmissions[];
  recruiterPerformance?: RecruiterPerformance[];
  pipelineHealth?: PipelineHealth;
  periodComparison?: PeriodComparison;
  qualityMetrics?: QualityMetrics;
  isLoading?: boolean;
}

export const EnhancedAnalyticsCharts: React.FC<EnhancedAnalyticsChartsProps> = ({
  clientBreakdown,
  fitmentScoreDistribution,
  dayWiseSubmissions,
  managerWiseSubmissions,
  recruiterPerformance = [],
  pipelineHealth,
  periodComparison,
  qualityMetrics,
  isLoading = false
}) => {
  const isDark = useIsDarkMode();
  const chartColors = getChartColors(isDark);
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16'];
  
  // Shared tooltip styles
  const tooltipStyle = {
    backgroundColor: chartColors.tooltipBg,
    border: `1px solid ${chartColors.tooltipBorder}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: chartColors.tooltipText,
    boxShadow: isDark 
      ? '0 10px 25px rgba(0, 0, 0, 0.5)' 
      : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
  };
  
  const labelStyle = { 
    color: chartColors.tooltipText, 
    fontWeight: 'bold' as const 
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        {/* Client Requirements - Full Width */}
        <Card className="glass-card">
          <CardHeader>
            <div className="animate-pulse">
              <div className="h-6 bg-glass-surface rounded mb-2"></div>
              <div className="h-4 bg-glass-surface rounded w-2/3"></div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="animate-pulse h-80 bg-glass-surface rounded"></div>
          </CardContent>
        </Card>
        
        {/* Fitment Score Distribution - Full Width */}
        <Card className="glass-card">
          <CardHeader>
            <div className="animate-pulse">
              <div className="h-6 bg-glass-surface rounded mb-2"></div>
              <div className="h-4 bg-glass-surface rounded w-2/3"></div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="animate-pulse h-80 bg-glass-surface rounded"></div>
          </CardContent>
        </Card>
        
        {/* Manager Performance */}
        <Card className="glass-card">
          <CardHeader>
            <div className="animate-pulse">
              <div className="h-6 bg-glass-surface rounded mb-2"></div>
              <div className="h-4 bg-glass-surface rounded w-2/3"></div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="animate-pulse h-80 bg-glass-surface rounded"></div>
          </CardContent>
        </Card>
        
        {/* Recruiter Performance - Admin/CEO only */}
        {(recruiterPerformance && recruiterPerformance.length > 0) && (
          <Card className="glass-card">
            <CardHeader>
              <div className="animate-pulse">
                <div className="h-6 bg-glass-surface rounded mb-2"></div>
                <div className="h-4 bg-glass-surface rounded w-2/3"></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="animate-pulse h-80 bg-glass-surface rounded"></div>
            </CardContent>
          </Card>
        )}
        
        {/* Pipeline Health - Admin/CEO only */}
        {pipelineHealth && (
          <Card className="glass-card">
            <CardHeader>
              <div className="animate-pulse">
                <div className="h-6 bg-glass-surface rounded mb-2"></div>
                <div className="h-4 bg-glass-surface rounded w-2/3"></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="animate-pulse h-80 bg-glass-surface rounded"></div>
            </CardContent>
          </Card>
        )}
        
        {/* Period Comparison - Admin/CEO only */}
        {periodComparison && (
          <Card className="glass-card">
            <CardHeader>
              <div className="animate-pulse">
                <div className="h-6 bg-glass-surface rounded mb-2"></div>
                <div className="h-4 bg-glass-surface rounded w-2/3"></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="animate-pulse h-80 bg-glass-surface rounded"></div>
            </CardContent>
          </Card>
        )}
        
        {/* Quality Metrics - Admin/CEO only */}
        {qualityMetrics && (
          <Card className="glass-card">
            <CardHeader>
              <div className="animate-pulse">
                <div className="h-6 bg-glass-surface rounded mb-2"></div>
                <div className="h-4 bg-glass-surface rounded w-2/3"></div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="animate-pulse h-80 bg-glass-surface rounded"></div>
            </CardContent>
          </Card>
        )}
        
        {/* Day-wise Uploads Trend - Full Width */}
        <Card className="glass-card">
          <CardHeader>
            <div className="animate-pulse">
              <div className="h-6 bg-glass-surface rounded mb-2"></div>
              <div className="h-4 bg-glass-surface rounded w-2/3"></div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="animate-pulse h-80 bg-glass-surface rounded"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if there are multiple clients - if so, make chart full width
  const hasMultipleClients = clientBreakdown.length > 1;

  return (
    <div className="space-y-8">
      {/* Row 1: Client Requirements - Full Width if Multiple Clients */}
        <Card className="glass-card hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-xl">
              <BarChart className="w-6 h-6 text-primary-500" />
              <span>Client-wise Requirements</span>
            </CardTitle>
            <CardDescription className="text-base">
            Number of requirements and submissions per client
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart data={clientBreakdown as any}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis 
                    dataKey="client" 
                    tick={{ fontSize: hasMultipleClients ? 14 : 16, fill: chartColors.text }}
                    angle={hasMultipleClients ? -45 : 0}
                    textAnchor={hasMultipleClients ? "end" : "middle"}
                    height={hasMultipleClients ? 100 : 60}
                    stroke={chartColors.textMuted}
                  />
                  <YAxis tick={{ fontSize: 16, fill: chartColors.text }} stroke={chartColors.textMuted} />
                  <Tooltip 
                    contentStyle={tooltipStyle}
                    labelStyle={labelStyle}
                  />
                  <Legend wrapperStyle={{ color: chartColors.text }} />
                  <Bar dataKey="requirements" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Requirements" />
                  <Bar dataKey="submissions" fill="#10B981" radius={[4, 4, 0, 0]} name="Submissions" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

      {/* Row 2: Fitment Score Distribution - Full Width */}
        <Card className="glass-card hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-xl">
            <BarChart3 className="w-6 h-6 text-accent-500" />
              <span>Fitment Score Distribution</span>
            </CardTitle>
            <CardDescription className="text-base">
            Distribution of fitment scores across all submissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart
                  data={fitmentScoreDistribution as any}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis type="number" tick={{ fontSize: 14, fill: chartColors.text }} stroke={chartColors.textMuted} />
                  <YAxis 
                    type="category" 
                    dataKey="range" 
                    tick={{ fontSize: 13, fill: chartColors.text }}
                    width={110}
                    stroke={chartColors.textMuted}
                  />
                  <Tooltip 
                    contentStyle={tooltipStyle}
                    labelStyle={labelStyle}
                    formatter={(_value: any, _name: string, props: any) => [
                      `${props.payload.count} submissions (${props.payload.percentage}%)`,
                      'Count'
                    ]}
                  />
                  <Bar 
                    dataKey="count" 
                    radius={[0, 8, 8, 0]}
                  >
                    {fitmentScoreDistribution.map((item: any, index: number) => {
                      // Use color gradient based on score range
                      let color = COLORS[index % COLORS.length];
                      const range = item.range || '';
                      if (range.includes('Excellent')) {
                        color = '#10B981'; // Green
                      } else if (range.includes('Good')) {
                        color = '#3B82F6'; // Blue
                      } else if (range.includes('Average')) {
                        color = '#F59E0B'; // Amber
                      } else if (range.includes('Below Average')) {
                        color = '#F97316'; // Orange
                      } else if (range.includes('Poor')) {
                        color = '#EF4444'; // Red
                      }
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Bar>
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

      {/* Row 3: Manager Performance */}
        <Card className="glass-card hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-xl">
              <BarChart3 className="w-6 h-6 text-accent-500" />
              <span>Manager Performance</span>
            </CardTitle>
            <CardDescription className="text-base">
              Manager-wise uploads with performance metrics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart data={managerWiseSubmissions as any}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis 
                    dataKey="manager" 
                    tick={{ fontSize: 16, fill: chartColors.text }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    stroke={chartColors.textMuted}
                  />
                  <YAxis tick={{ fontSize: 16, fill: chartColors.text }} stroke={chartColors.textMuted} />
                  <Tooltip 
                    contentStyle={tooltipStyle}
                    labelStyle={labelStyle}
                  />
                  <Bar dataKey="submissions" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Submissions" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

      {/* Row 4: Recruiter Performance - Full Width (Admin/CEO only) */}
      {recruiterPerformance && recruiterPerformance.length > 0 && (
        <Card className="glass-card hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-xl">
              <Users className="w-6 h-6 text-primary-500" />
              <span>Recruiter Performance</span>
            </CardTitle>
            <CardDescription className="text-base">
              Recruiter-wise submissions and performance metrics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart 
                  data={recruiterPerformance.map(r => ({
                    recruiter: r.recruiter.length > 15 ? r.recruiter.substring(0, 15) + '...' : r.recruiter,
                    submissions: r.totalSubmissions,
                    manager: r.manager
                  })) as any}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis 
                    dataKey="recruiter" 
                    tick={{ fontSize: 12, fill: chartColors.text }}
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    stroke={chartColors.textMuted}
                  />
                  <YAxis tick={{ fontSize: 14, fill: chartColors.text }} stroke={chartColors.textMuted} />
                  <Tooltip 
                    contentStyle={tooltipStyle}
                    labelStyle={labelStyle}
                    formatter={(value: any, name: string) => [
                      `${value} submissions`,
                      name === 'submissions' ? 'Submissions' : name
                    ]}
                  />
                  <Bar dataKey="submissions" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Submissions" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Row 5: Pipeline Health - Full Width (Admin/CEO only) */}
      {pipelineHealth && (
        <Card className="glass-card hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-xl">
              <Activity className="w-6 h-6 text-success-500" />
              <span>Pipeline Health</span>
            </CardTitle>
            <CardDescription className="text-base">
              Distribution of submissions across pipeline stages
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart
                  data={[
                    { name: 'Submitted', value: pipelineHealth.submitted, fill: '#3B82F6' },
                    { name: 'Reviewing', value: pipelineHealth.reviewing, fill: '#F59E0B' },
                    { name: 'Shortlisted', value: pipelineHealth.shortlisted, fill: '#10B981' },
                    { name: 'Rejected', value: pipelineHealth.rejected, fill: '#EF4444' },
                    { name: 'Withdrawn', value: pipelineHealth.withdrawn, fill: '#9CA3AF' }
                  ]}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 80, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis type="number" tick={{ fontSize: 14, fill: chartColors.text }} stroke={chartColors.textMuted} />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fontSize: 14, fill: chartColors.text }}
                    width={80}
                    stroke={chartColors.textMuted}
                  />
                  <Tooltip 
                    contentStyle={tooltipStyle}
                    labelStyle={labelStyle}
                    formatter={(value: any) => `${value} candidates`}
                  />
                  <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                    {[
                      { name: 'Submitted', fill: '#3B82F6' },
                      { name: 'Reviewing', fill: '#F59E0B' },
                      { name: 'Shortlisted', fill: '#10B981' },
                      { name: 'Rejected', fill: '#EF4444' },
                      { name: 'Withdrawn', fill: '#9CA3AF' }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Row 6: Period Comparison - Full Width (Admin/CEO only) */}
      {periodComparison && (
        <Card className="glass-card hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-xl">
              <TrendingUp className="w-6 h-6 text-accent-500" />
              <span>Period Comparison</span>
            </CardTitle>
            <CardDescription className="text-base">
              Current period vs previous period metrics comparison
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsBarChart
                  data={[
                    {
                      metric: 'Submissions',
                      current: periodComparison.current_period.total_submissions,
                      previous: periodComparison.previous_period.total_submissions,
                      change: periodComparison.percentage_change.total_submissions
                    },
                    {
                      metric: 'Active Reqs',
                      current: periodComparison.current_period.active_requirements,
                      previous: periodComparison.previous_period.active_requirements,
                      change: periodComparison.percentage_change.active_requirements
                    },
                    {
                      metric: 'Avg Fitment',
                      current: periodComparison.current_period.avg_fitment_score,
                      previous: periodComparison.previous_period.avg_fitment_score,
                      change: periodComparison.percentage_change.avg_fitment_score
                    },
                    {
                      metric: 'Shortlisted',
                      current: periodComparison.current_period.shortlisted,
                      previous: periodComparison.previous_period.shortlisted,
                      change: periodComparison.percentage_change.shortlisted
                    }
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis 
                    dataKey="metric" 
                    tick={{ fontSize: 14, fill: chartColors.text }}
                    stroke={chartColors.textMuted}
                  />
                  <YAxis tick={{ fontSize: 14, fill: chartColors.text }} stroke={chartColors.textMuted} />
                  <Tooltip 
                    contentStyle={tooltipStyle}
                    labelStyle={labelStyle}
                    formatter={(value: any, name: string) => {
                      if (name === 'change') {
                        const trend = value >= 0 ? '↑' : '↓';
                        return [`${trend} ${Math.abs(value).toFixed(1)}%`, 'Change'];
                      }
                      return [value, name === 'current' ? 'Current Period' : 'Previous Period'];
                    }}
                  />
                  <Legend wrapperStyle={{ color: chartColors.text }} />
                  <Bar dataKey="current" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Current Period" />
                  <Bar dataKey="previous" fill="#9CA3AF" radius={[4, 4, 0, 0]} name="Previous Period" />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Row 7: Quality Metrics - Full Width (Admin/CEO only) */}
      {qualityMetrics && (
        <Card className="glass-card hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-xl">
              <Target className="w-6 h-6 text-accent-500" />
              <span>Quality Metrics</span>
            </CardTitle>
            <CardDescription className="text-base">
              Quality indicators and score trends
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 dark:from-blue-500/30 dark:to-blue-600/20 border border-blue-200 dark:border-blue-500/30 rounded-xl p-5">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">Average Fitment Score</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{qualityMetrics.avg_fitment_score.toFixed(1)}%</p>
                <div className="flex items-center mt-2">
                  <TrendingUp className={`w-4 h-4 mr-1 ${qualityMetrics.score_trend >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                  <span className={`text-sm font-medium ${qualityMetrics.score_trend >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {qualityMetrics.score_trend >= 0 ? '+' : ''}{qualityMetrics.score_trend.toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 dark:from-emerald-500/30 dark:to-emerald-600/20 border border-emerald-200 dark:border-emerald-500/30 rounded-xl p-5">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">High Quality Rate</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{qualityMetrics.high_quality_rate.toFixed(1)}%</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Candidates with &gt;80% fitment</p>
              </div>
            </div>
            {qualityMetrics.common_skill_gaps && qualityMetrics.common_skill_gaps.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Common Skill Gaps</p>
                <div className="flex flex-wrap gap-2">
                  {qualityMetrics.common_skill_gaps.slice(0, 5).map((gap, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1.5 bg-amber-100 dark:bg-amber-500/20 border border-amber-200 dark:border-amber-500/30 rounded-full text-sm text-amber-800 dark:text-amber-300 font-medium"
                    >
                      {gap.skill} ({gap.count})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Row 8: Day-wise Uploads Trend - Full Width */}
      <Card className="glass-card hover-lift">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-xl">
            <LineChart className="w-6 h-6 text-secondary-500" />
            <span>Day-wise Uploads Trend</span>
          </CardTitle>
          <CardDescription className="text-base">
            {dayWiseSubmissions.length > 30 
              ? `Showing last 30 days of ${dayWiseSubmissions.length} total days`
              : `Uploads trend for ${dayWiseSubmissions.length} days`
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsLineChart data={
                // Limit to last 30 days for better readability
                dayWiseSubmissions.length > 30 
                  ? (dayWiseSubmissions as any).slice(-30) 
                  : dayWiseSubmissions as any
              }>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12, fill: chartColors.text }}
                  tickFormatter={(value: string) => {
                    // Format date as "Jan 15" instead of "2026-01-15"
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }}
                  interval={Math.max(0, Math.floor((Math.min(dayWiseSubmissions.length, 30) - 1) / 7))}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  stroke={chartColors.textMuted}
                />
                <YAxis 
                  tick={{ fontSize: 14, fill: chartColors.text }} 
                  stroke={chartColors.textMuted}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={tooltipStyle}
                  labelStyle={labelStyle}
                  labelFormatter={(value: string) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="submissions" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6', strokeWidth: 1, r: 3 }}
                  activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2 }}
                  name="Submissions"
                />
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnhancedAnalyticsCharts;
